import React, { useState, useRef, useEffect, useMemo } from 'react';

const CustomNameSuggestion = ({ label, options = [], onChange, value, name, placeholder, error, id }) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();

  // The field is driven from the parent too (loading a record, clearing a form).
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  // Only the options matching what has been typed, so a long list - every
  // patient on file, say - stays usable instead of rendering in full.
  const matches = useMemo(() => {
    const query = String(inputValue ?? '').toLowerCase().trim();
    const list = options?.filter?.(Boolean) || [];
    if (!query) return list;
    return list.filter((item) => String(item).toLowerCase().includes(query));
  }, [options, inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange?.(event);
    openDropdown();
  };

  const handleOptionClick = (optionValue) => {
    setInputValue(optionValue);
    onChange?.({ target: { name, value: optionValue } });
    closeDropdown();
  };

  const openDropdown = () => {
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className="mt-5">
      <label className="text-sm font-medium text-primary-900 mb-1">{label}</label>
      <div className="relative" ref={dropdownRef}>
        <input id={id} type="text" value={inputValue} name={name} onChange={handleInputChange} onFocus={openDropdown} placeholder={placeholder} className="w-full border border-primary-300 text-primary-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 p-2.5" />
        {isOpen && (
          <div className="absolute top-full left-0 w-full max-h-60 overflow-y-auto bg-white border border-primary-300 rounded-b-lg mt-1 z-10 shadow-lg">
            {matches.length ? (
              matches.map((item, index) => (
                <div key={index} className="p-2.5 cursor-pointer hover:bg-primary-100" onClick={() => handleOptionClick(item)}>
                  {item}
                </div>
              ))
            ) : (
              <p className="p-2.5 text-xs text-gray-400">No matches</p>
            )}
          </div>
        )}
      </div>
      <span className="text-xs font-medium text-red-400">{error}</span>
    </div>
  );
};

export default CustomNameSuggestion;
